import React from 'react';
import { tr } from '../../i18n/i18n';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTES } from '../../constants';
import { getFeatureMetadata } from '../../constants/featureRegistry';

export interface FeatureBlockedPageProps {
  /** The subscription feature code (e.g. 'library_management'). Title and description are
   *  resolved automatically from the feature registry — override with the props below if needed. */
  featureCode: string;
  /** Optional override for the lock title (defaults to registry value). */
  featureName?: string;
  /** Optional override for the lock description (defaults to registry value). */
  description?: string;
}

const FeatureBlockedPage: React.FC<FeatureBlockedPageProps> = ({
  featureCode,
  featureName,
  description,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const metadata = getFeatureMetadata(featureCode);
  // Title/description are translated by feature code (falls back to the registry English)
  const title = featureName ?? t(`feature.${featureCode}.title`, metadata.title);
  const subtitle =
    description ?? t(`feature.${featureCode}.description`, t('feature.generic_description', metadata.description));

  const handleUpgrade = () => {
    navigate(`${APP_ROUTES.BILLING_AND_PLANS}?tab=plans`);
  };

  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center"
      style={{
        backdropFilter: 'blur(5.75px)',
        WebkitBackdropFilter: 'blur(5.75px)',
        backgroundColor: 'rgba(255, 255, 255, 0.89)',
        zIndex: 10,
      }}
    >
      <div
        className="flex flex-col items-center rounded-xl w-[423px] py-9"
        style={{ backgroundColor: 'rgba(255, 255, 255, 0.56)' }}
      >
        <div className="flex flex-col gap-[11px] items-center px-[10px] py-4 w-full">
          {/* Lock icon - mynaui:lock (open padlock) */}
          <div className="flex items-center justify-center" style={{ height: 64, width: 62 }}>
            <svg
              width="56"
              height="56"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7 10.0288C7.47142 10 8.05259 10 8.8 10H15.2C15.9474 10 16.5286 10 17 10.0288M7 10.0288C6.41168 10.0647 5.99429 10.1455 5.63803 10.327C5.07354 10.6146 4.6146 11.0735 4.32698 11.638C4 12.2798 4 13.1198 4 14.8V16.2C4 17.8802 4 18.7202 4.32698 19.362C4.6146 19.9265 5.07354 20.3854 5.63803 20.673C6.27976 21 7.11984 21 8.8 21H15.2C16.8802 21 17.7202 21 18.362 20.673C18.9265 20.3854 19.3854 19.9265 19.673 19.362C20 18.7202 20 17.8802 20 16.2V14.8C20 13.1198 20 12.2798 19.673 11.638C19.3854 11.0735 18.9265 10.6146 18.362 10.327C18.0057 10.1455 17.5883 10.0647 17 10.0288M7 10.0288V7C7 4.23858 9.23858 2 12 2C14.2822 2 16.2165 3.53389 16.8054 5.63801"
                stroke="#374151"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 14.5V16.5"
                stroke="#374151"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {/* Title + subtitle */}
          <div className="flex flex-col gap-1 items-center w-full">
            <h2 className="text-[22px] font-bold text-black text-center w-full leading-[30px]">
              {t('ui.unlock', 'Unlock')} {title}
            </h2>
            <p
              className="text-[16px] font-medium text-center w-full leading-[24px]"
              style={{ color: 'rgba(0, 0, 0, 0.66)' }}
            >
              {subtitle}
            </p>
          </div>

          {/* Upgrade button */}
          <div className="w-full px-[76px]">
            <button
              onClick={handleUpgrade}
              className="w-full h-[40px] rounded-[8px] text-white text-[14px] font-bold leading-[21px]"
              style={{ backgroundColor: '#69578f' }}
            >
              {t('ui.upgrade_now', 'Upgrade Now')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureBlockedPage;
